/**
 * MyRewardsPage — standalone "Mis recompensas" section
 * (DASHBOARD-SIDEMENU-PROFILES-1-FIX1 · Feb 2026).
 *
 * Hosts the Reward Points balance + activity list that previously lived
 * in the dashboard's right aside. Moved here so the dashboard surface
 * stays focused and the side menu has a real destination for the new
 * `Mis recompensas` entry.
 *
 * Visibility: identical to the previous aside — hidden for admin /
 * staff / QA / institutional_observer roles (they don't see a points
 * balance on the public panel). Non-staff users are routed here from
 * the side menu.
 *
 * Frontend-only · uses the existing `/me/rewards` API (read-only).
 * No DB writes from this page.
 */
import React, { useEffect, useState } from "react";
import PanelLayout from "@/components/layout/PanelLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { Navigate } from "react-router-dom";
import { Award } from "lucide-react";

const STAFF_ROLE_KEYS = new Set([
  "admin",
  "staff",
  "qa_manager",
  "qa_tester",
  "institutional_observer",
]);

const PAGE_COPY = {
  es: {
    eyebrow: "Sección",
    title: "Mis recompensas",
    subtitle: "Tu saldo de puntos y todo el histórico de movimientos.",
    balanceLabel: "Saldo de puntos",
    activityTitle: "Mi actividad",
    activityEmpty: "Sin movimientos aún.",
    historyOf: "Historial completo · {n} movimientos",
  },
  en: {
    eyebrow: "Section",
    title: "My rewards",
    subtitle: "Your points balance and the full activity history.",
    balanceLabel: "Points balance",
    activityTitle: "My activity",
    activityEmpty: "No activity yet.",
    historyOf: "Full history · {n} entries",
  },
};

export default function MyRewardsPage() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const copy = PAGE_COPY[lang] || PAGE_COPY.en;
  const [rewards, setRewards] = useState({ events: [], totals: {} });

  useEffect(() => {
    api.get("/me/rewards").then((r) => setRewards(r.data)).catch(() => {});
  }, []);

  if (user && STAFF_ROLE_KEYS.has(user.role)) {
    return <Navigate to="/app" replace />;
  }

  const total = rewards.totals?.reward_points ?? user?.reward_points ?? 0;
  const events = rewards.events || [];

  return (
    <PanelLayout>
      <div data-testid="page-my-rewards">
        <header className="mb-2 flex items-center gap-3">
          <Award className="w-6 h-6 text-emerald-700" />
          <div className="tsw-overline">{copy.eyebrow}</div>
        </header>
        <h1
          className="text-2xl font-bold text-slate-900"
          data-testid="page-my-rewards-title"
        >
          {copy.title}
        </h1>
        <p className="text-sm text-slate-600 mt-1 mb-6">{copy.subtitle}</p>

        <div className="grid lg:grid-cols-3 gap-6">
          <div
            className="tsw-card p-5 bg-emerald-50 border-emerald-100 lg:col-span-1"
            data-testid="reward-points-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
                <Award className="w-5 h-5" />
              </div>
              <div>
                <div className="text-emerald-700 text-[10px] font-bold uppercase tracking-widest">
                  {copy.balanceLabel}
                </div>
                <div
                  className="font-display font-bold text-3xl text-emerald-900 leading-none mt-1"
                  data-testid="reward-total"
                >
                  {total}
                </div>
              </div>
            </div>
          </div>

          <div className="tsw-card p-6 lg:col-span-2" data-testid="page-my-rewards-activity-card">
            <div className="font-semibold text-slate-900 mb-3">
              {copy.activityTitle}
            </div>
            <ul
              className="divide-y divide-slate-100"
              data-testid="reward-activity-list"
            >
              {events.length === 0 && (
                <li className="text-slate-400 text-sm py-2">{copy.activityEmpty}</li>
              )}
              {events.map((e) => {
                const evKey = e.event_type || e.reason || "";
                const translated = t(`user.rewardEvents.${evKey}`);
                const label =
                  translated && translated !== `user.rewardEvents.${evKey}`
                    ? translated
                    : evKey.replaceAll("_", " ");
                return (
                  <li
                    key={e.id || `${evKey}-${e.created_at || ""}`}
                    className="flex items-center justify-between text-sm py-2"
                  >
                    <span data-testid={`reward-event-${evKey}`}>{label}</span>
                    <span
                      className={
                        (e.points || 0) < 0
                          ? "text-rose-700 font-medium"
                          : "text-emerald-700 font-medium"
                      }
                    >
                      {(e.points || 0) >= 0 ? `+${e.points}` : e.points}
                    </span>
                  </li>
                );
              })}
            </ul>
            {events.length > 0 && (
              <p className="mt-3 text-[11px] text-slate-500" data-testid="reward-history-count">
                {copy.historyOf.replace(
                  "{n}",
                  String(rewards.history_count ?? events.length),
                )}
              </p>
            )}
          </div>
        </div>
      </div>
    </PanelLayout>
  );
}
